import type { ParsedUrlQuery } from "querystring";

import { Currency } from "@/Apollo/schema";
import { PROMOTIONS_ENABLED } from "@/features/promotions/config";
import { searchClient } from "@/features/search/api/client";
import type { PageParams, SearchParams } from "@/features/search/api/queries";
import { buildSearchParams } from "@/features/search/utils/search-params.utils";
import { toHref } from "@/features/search/utils/url.utils";
import { withUsShippableDefault } from "@/features/us-market/us-shippable";
import { queryOptions } from "@tanstack/react-query";

/**
 * One route, two kinds of URL.
 *
 * `pages/[...search].tsx` is the site's catch-all, so it has to serve both:
 *
 *   /search?query=chair        query-driven search    ->  GET /search
 *   /furniture/dining-chairs   curated landing page   ->  GET /pages?url=…
 *
 * Both endpoints answer with the same `SearchResponse`, so resolving which one a URL
 * means is the *only* place that difference exists — facets, results, pagination and
 * breadcrumbs never learn about it.
 */

/** Catch-all segment name — must match the `[...search]` filename. */
export const SEARCH_ROUTE_PARAM = "search";

/** The single path treated as a query search; everything else is a landing page. */
export const SEARCH_PATH = `/${SEARCH_ROUTE_PARAM}`;

export type SearchRoute =
  | { kind: "search"; params: SearchParams }
  | { kind: "page"; params: PageParams };

const segmentsToPath = (segments: string | string[] | undefined) =>
  toHref(Array.isArray(segments) ? segments.join("/") : segments ?? "");

/** Resolve a Next.js router query into the API call its URL stands for. */
export const resolveSearchRoute = (
  query: ParsedUrlQuery,
  locale?: string
): SearchRoute => {
  const { [SEARCH_ROUTE_PARAM]: segments, ...filters } = query;
  const path = segmentsToPath(segments);
  /* WHO-6510: on `/en-us`, only what ships to the US unless the URL asks for all items. */
  const params = withUsShippableDefault(
    buildSearchParams(filters, locale),
    filters,
    locale
  );

  if (path === SEARCH_PATH) {
    return { kind: "search", params };
  }

  /*
   * `url` identifies the landing page; the remaining params narrow it exactly as they
   * narrow a search. Cast because the two generated param types differ only in the
   * search-only keys, which `/pages` ignores.
   */
  return { kind: "page", params: { ...params, url: path } as PageParams };
};

/**
 * Cache root for result queries, both endpoints. Every wholesale update goes through it —
 * `invalidateQueries` on a viewer change, `setQueriesData` when a favorite is toggled — so it is the
 * one place that knows the key shape. Nothing else may be keyed under this root (see
 * `suggestionsQueryOptions`, which has its own).
 */
export const SEARCH_RESULTS_QUERY_KEY = ["search"] as const;

/**
 * How long the server render waits on the search service before giving up. The request had no
 * limit, so a slow service held the render until the platform killed it and Googlebot logged a
 * 5xx after ~30 s (GSC, 22 to 29 Sept 2026). Past this the page takes the "search failed" path
 * `SearchPage.server.ts` already has: a 503 with `no-store` on a landing page (WHO-6050), an empty
 * grid the client refetches on /search.
 */
export const SEARCH_SSR_TIMEOUT_MS = 8000;

/**
 * The search service answered with an error status. The status is kept so the server render can
 * tell "no such landing page" (404) from an outage (5xx), which must never be cached as a 404.
 */
export class SearchServiceError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown
  ) {
    super(`search service answered ${status}`);
    this.name = "SearchServiceError";
  }
}

/** The route's params with the display currency, when there is one (omitted: the listing's own). */
const withSearchCurrency = (route: SearchRoute, currency?: Currency) =>
  currency ? { ...route.params, currency } : route.params;

/**
 * The query for a resolved route. Shared by `getServerSideProps` and `SearchProvider`,
 * so the server prefetch and the client query hit the same cache key and hydration
 * never triggers a refetch.
 *
 * `token` fills each result's `favoriteId` — the service resolves it from the bearer token — and it
 * stays out of the key for two reasons. It would ship to the browser inside the dehydrated cache;
 * and the key has to be *identical* on both sides of hydration, which rules out anything auth-shaped:
 * `authStore` reads its values with js-cookie, which no-ops without a `document`, so during SSR the
 * React tree always looks anonymous even when the prefetch was authenticated. Keying on the viewer
 * therefore made the server miss its own dehydrated entry and render an empty page for signed-in
 * visitors. `useSearchResults` refetches on a viewer change instead.
 */
export const searchRouteQueryOptions = (
  route: SearchRoute,
  token?: string | null,
  /** Server only: see `SEARCH_SSR_TIMEOUT_MS`. The browser keeps react-query's own signal. */
  timeoutMs?: number,
  /**
   * The buyer's display currency (WHO-5142): prices, the price facet and the price filter come
   * back in it, and `listingPrices` carries the euros. It is part of the request, so it is part of
   * the key. Browser only until WHO-5143 gives the server the same cookie; until then a page with
   * a display currency fetches again after hydration.
   */
  currency?: Currency
) => {
  const params = withSearchCurrency(route, currency);

  return queryOptions({
    queryKey: [...SEARCH_RESULTS_QUERY_KEY, route.kind, params] as const,
    queryFn: async ({ signal: querySignal }) => {
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
      const signal = timeoutMs
        ? AbortSignal.any([querySignal, AbortSignal.timeout(timeoutMs)])
        : querySignal;

      const { data, error, response } =
        route.kind === "page"
          ? await searchClient.GET("/pages", {
              params: { query: params as PageParams },
              headers,
              signal,
              cache: PROMOTIONS_ENABLED ? "no-store" : undefined,
            })
          : await searchClient.GET("/search", {
              params: { query: params as SearchParams },
              headers,
              signal,
              cache: PROMOTIONS_ENABLED ? "no-store" : undefined,
            });

      if (error) {
        throw new SearchServiceError(response.status, error);
      }

      return data;
    },
  });
};
